import { ref, shallowRef, watch } from 'vue'
import { defineStore } from 'pinia'
import type { MenuItem, SubItem } from '../types/sidebar.d'
import { useRouter } from 'vue-router'
import { api } from '@/plugins/axios'
import { EXPORT_API_CONFIG } from '@/constants/api'
import { useMenuColor } from '@/composables/useMenuColor'
import { TitleResolver } from '@/utils/titleResolver'

const MENU_STORAGE_KEY = 'sidebar_menu_items'
const COLORS_STORAGE_KEY = 'sidebar_menu_colors'
const SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed'

export const useSidebar = (id: string | number) => {
  const store = defineStore(`sidebar-${id}`, () => {
    // vars --------------------------------
    // Desktop rail (icon-only) state. UI preference like the i18n locale key,
    // so it intentionally survives logout and is NOT cleared in onLogout().
    const collapsed = ref(localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1')
    const router = useRouter()
    const title = shallowRef<string>('menu.dashboard')
    const dynamicTitle = shallowRef<string | null>(null)
    const items = shallowRef<MenuItem[]>([])
    const menuColors = ref<Map<string, string>>(new Map())
    const isInitialized = ref(false)

    // Resolves route paths against the API menu: exact match → route meta →
    // menu ancestor (segment strip) → default
    const titleResolver = new TitleResolver([])

    // composables --------------------------------
    const { getPathColor } = useMenuColor(menuColors)

    function setTitle(newTitle: string) {
      title.value = newTitle
    }

    /**
     * The only sanctioned channel for page-set dynamic titles (e.g. a form's
     * API `page_title`). Cleared automatically on every route change.
     */
    function setDynamicTitle(newTitle: string | null) {
      dynamicTitle.value = newTitle
    }

    function setCollapsed(value: boolean) {
      collapsed.value = value
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, value ? '1' : '0')
    }

    async function fetchMenu() {
      try {
        const response = await api.get<{ data: MenuItem[] }>(EXPORT_API_CONFIG.GET_MENU)
        const menuData = response.data?.data || response.data
        if (menuData && Array.isArray(menuData)) {
          items.value = menuData
          updateMenuColors(menuData)
          titleResolver.updateMenu(menuData) // Update resolver with new menu data
          localStorage.setItem(MENU_STORAGE_KEY, JSON.stringify(menuData))
          localStorage.setItem(COLORS_STORAGE_KEY, JSON.stringify([...menuColors.value.entries()]))
          isInitialized.value = true
          // Update title after menu is loaded
          updateTitleFromCurrentPath()
        }
      } catch (error) {
        console.error('Error fetching menu:', error)
        // Even if menu fetch fails, meta/default resolution still applies
        updateTitleFromCurrentPath()
      }
    }

    function updateMenuColors(menuItems: MenuItem[]) {
      menuColors.value.clear()
      menuItems.forEach((item) => {
        if (item.color) menuColors.value.set(item.path, item.color)
        item.items?.forEach((subItem: SubItem) => {
          if (subItem.color) menuColors.value.set(subItem.path, subItem.color)
        })
      })
    }

    function initialize() {
      const storedItems = localStorage.getItem(MENU_STORAGE_KEY)
      const storedColors = localStorage.getItem(COLORS_STORAGE_KEY)
      if (storedItems && storedColors) {
        const parsedItems = JSON.parse(storedItems)
        items.value = parsedItems
        menuColors.value = new Map(JSON.parse(storedColors))
        titleResolver.updateMenu(parsedItems) // Update resolver with stored menu data
        isInitialized.value = true
        updateTitleFromCurrentPath()
      }
    }

    function updateTitleFromCurrentPath() {
      const current = router.currentRoute.value
      setTitle(titleResolver.resolve(current.path, current.meta?.title))
    }

    async function onLogin() {
      // gorji's menu is under active development — always track the backend
      // instead of hydrating from the localStorage cache.
      if (CONFIG.DIR === 'gorji') {
        await fetchMenu()
      } else if (!localStorage.getItem(MENU_STORAGE_KEY)) {
        await fetchMenu()
      } else {
        initialize()
      }
      // Set title based on the current route's meta/menu
      updateTitleFromCurrentPath()
    }

    function onLogout() {
      localStorage.removeItem(MENU_STORAGE_KEY)
      localStorage.removeItem(COLORS_STORAGE_KEY)
      items.value = []
      menuColors.value.clear()
      isInitialized.value = false
      dynamicTitle.value = null
      title.value = 'menu.dashboard'
    }

    // watch --------------------------------
    watch(
      () => router.currentRoute.value.path,
      () => {
        // Page-set dynamic titles only survive until the next navigation.
        // Meta/default resolution never depends on the menu, so resolve
        // immediately; the isInitialized watcher below upgrades the title to
        // the exact menu label once the menu arrives.
        dynamicTitle.value = null
        updateTitleFromCurrentPath()
      },
      { immediate: true },
    )

    // Watch for initialization changes to update title
    watch(isInitialized, (newValue) => {
      if (newValue) {
        updateTitleFromCurrentPath()
      }
    })

    initialize()

    return {
      collapsed,
      items,
      title,
      dynamicTitle,
      getPathColor,
      setCollapsed,
      setDynamicTitle,
      fetchMenu,
      onLogin,
      onLogout,
    }
  })

  return store
}
